import React, { useCallback, useEffect, useState } from 'react';

import { replaceContents } from '@utils/replaceContents';
import styled from 'styled-components';
import UserAvatar from '@components/dataDisplay/Avatar/UserAvatar';
import Icon from '@components/dataDisplay/Icons/Icon';
import { Link } from 'react-router-dom';
import { CollectionPath, SettingCollectionPath } from '@typings/collections.enum';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { chatDetailIdState } from '@atom/setting/navigation';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import { readChatCheck } from '@store/firebase/chat/add-chat';
import { currentUserIDState } from '@atom/setting/user';
import { useNotificationSound } from '@hooks/useNotificationSound';
import {
  notificationSettingsState,
  lastNotificationTimeState,
  chatMessageNotificationState,
} from '@atom/setting/notification';
import SnackBar from '@components/dataDisplay/Snackbar';
import { useDocument } from '@nandorojo/swr-firestore';
import { ISoundEffect } from '@typings/setting/soundEffect.interface';
import { LOCAL_FALLBACK_URL } from '@pages/Setting/3.Homepage/Notifications/NotificationSoundPage';

const HeaderSec = styled.div`
  display: flex;
  justify-content: space-between;
`;

const MessagePreview = styled.div`
  padding-top: 8px;
  width: 300px;
  text-align: left;
  font-size: 14px;

  * {
    line-height: 1.3;
    /* ellipsis */
    min-height: 0;
    max-height: 40px;
    overflow: hidden;
    white-space: normal;
    text-overflow: ellipsis;
    word-wrap: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    /* ellipsis end */
  }
`;

type Props = {};
function ChatMessageNotification({}: Props) {
  const setChatDetailId = useSetRecoilState(chatDetailIdState);
  const setLastNotificationTime = useSetRecoilState(lastNotificationTimeState);
  const currentUserUid = useRecoilValue(currentUserIDState);
  const notificationSettings = useRecoilValue(notificationSettingsState);
  const lastNotificationTime = useRecoilValue(lastNotificationTimeState);
  const [notiData, setNotiData] = useRecoilState(chatMessageNotificationState);

  // Firestore 알림음 URL (Notifications.tsx와 동일한 소스)
  const { data: soundEffect } = useDocument<ISoundEffect>(
    SettingCollectionPath.SOUNDEFFECT,
    { listen: true },
  );
  const effectiveSoundUrl =
    soundEffect?.notificationSound?.url || LOCAL_FALLBACK_URL;

  const [showNoti, setShowNoti] = useState(false);
  const [prevNotiShow, setPrevNotiShow] = useState(false); // 🔧 이전 알림 상태 추적
  const [prevMessageId, setPrevMessageId] = useState<string>(''); // 🔧 이전 메시지 ID 추적
  const [prevUnreadCount, setPrevUnreadCount] = useState<number>(0); // �� 이전 읽지 않은 메시지 수 추적

  // 🔧 컴포넌트 마운트 시 초기화
  useEffect(() => {
    // lastNotificationTime이 0이면 현재 시간으로 초기화 (첫 진입 시 알림 방지)
    if (lastNotificationTime === 0) {
      setLastNotificationTime(Date.now());
      if (process.env.NODE_ENV === 'development') {
        console.log('🕐 lastNotificationTime 초기화:', Date.now());
      }
    }

    // 🔧 브라우저 알림 권한 요청 (사용자가 알림을 활성화한 경우)
    if (notificationSettings.systemNotification && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission().then((permission) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔔 브라우저 알림 권한:', permission);
          }
        });
      }
    }
  }, [
    lastNotificationTime,
    setLastNotificationTime,
    notificationSettings.systemNotification,
  ]); // 🔧 필요한 의존성 추가

  useEffect(() => {
    // chatNotification이 꺼진 경우 팝업 자체를 표시하지 않음
    setShowNoti(notiData.show && notificationSettings.chatNotification);
  }, [notiData, notificationSettings.chatNotification]);

  // 알림소리 훅 초기화 (Firestore soundEffect URL 우선 사용)
  const { notify } = useNotificationSound({
    volume: notificationSettings.volume,
    soundUrl: effectiveSoundUrl,
    enableSound:
      notificationSettings.soundEnabled &&
      notificationSettings.chatNotification,
    enableBrowserNotification: notificationSettings.systemNotification,
  });

  // 🔧 실제 새로운 알림이 발생했을 때만 브라우저 알림 처리
  useEffect(() => {
    const currentMessageId = notiData.latestDoc?.id || '';
    const currentUnreadCount = notiData.unRead?.length || 0;

    // 🔧 다양한 알림 조건들
    const isNewNotification = !prevNotiShow && notiData.show; // 새로운 알림 창 표시
    const isNewMessage = currentMessageId && currentMessageId !== prevMessageId; // 새로운 메시지
    const isUnreadCountIncreased = currentUnreadCount > prevUnreadCount; // 읽지 않은 메시지 증가

    // 🔧 알림 발생 조건: 위 조건 중 하나라도 만족하고, 메시지 내용이 있는 경우
    const shouldNotify =
      (isNewNotification || isNewMessage || isUnreadCountIncreased) &&
      notiData.show &&
      notiData.latestDoc?.contents;

    // if (process.env.NODE_ENV === 'development') {
    //   console.log('🔍 채팅 알림 조건 체크:', {
    //     isNewNotification,
    //     isNewMessage,
    //     isUnreadCountIncreased,
    //     shouldNotify,
    //     currentMessageId: currentMessageId.substring(0, 8) + '...',
    //     prevMessageId: prevMessageId.substring(0, 8) + '...',
    //     currentUnreadCount,
    //     prevUnreadCount,
    //     notiShow: notiData.show
    //   });
    // }

    if (shouldNotify) {
      const now = Date.now();
      const timeSinceLastNotification = now - lastNotificationTime;

      // 🔧 중복 알림 방지 시간을 1초로 단축 (더 빠른 연속 알림 허용)
      if (timeSinceLastNotification > 1000) {
        setLastNotificationTime(now);

        if (notificationSettings.chatNotification) {
          // 🔧 통합 알림 (소리 + 브라우저 알림)
          const cleanText = notiData.latestDoc.contents
            .replace(/<[^>]*>/g, '')
            .trim();
          const notificationTitle =
            currentUnreadCount > 1
              ? `새 채팅 메시지 (${currentUnreadCount}개)`
              : '새 채팅 메시지';

          notify({
            title: notificationTitle,
            body:
              cleanText.length > 0
                ? cleanText.substring(0, 100) +
                  (cleanText.length > 100 ? '...' : '')
                : '새 채팅 메시지가 도착했습니다.',
            tag: `chat-message-${currentMessageId}-${now}`,
            onClick: () => {
              console.log('채팅 알림 클릭됨');
            },
          });
        }

        // if (process.env.NODE_ENV === 'development') {
        //   console.log('✅ 통합 알림 실행:', {
        //     title: notificationTitle,
        //     tag: `chat-message-${currentMessageId}-${now}`,
        //     id: currentMessageId.substring(0, 8),
        //     preview: cleanText.substring(0, 30)
        //   });
        // }
      } else if (process.env.NODE_ENV === 'development') {
        // console.log('🔕 채팅 알림 스킵 (너무 빠른 연속 알림):', timeSinceLastNotification);
      }
    } else if (process.env.NODE_ENV === 'development') {
      // 🔧 알림이 스킵된 이유를 더 상세히 로깅
      if (!notiData.show) {
        // console.log('🔕 채팅 알림 스킵 (notiData.show = false)');
      } else if (!notiData.latestDoc?.contents) {
        // console.log('🔕 채팅 알림 스킵 (메시지 내용 없음)');
      } else if (
        !isNewNotification &&
        !isNewMessage &&
        !isUnreadCountIncreased
      ) {
        // console.log('🔕 채팅 알림 스킵 (조건 미충족)', {
        //   isNewNotification,
        //   isNewMessage,
        //   isUnreadCountIncreased
        // });
      }
    }

    // 🔧 이전 상태들 업데이트
    setPrevNotiShow(notiData.show);
    setPrevMessageId(currentMessageId);
    setPrevUnreadCount(currentUnreadCount);
  }, [
    notiData.show,
    notiData.latestDoc?.id,
    notiData.latestDoc?.contents,
    notiData.unRead?.length,
    notify,
    notificationSettings.systemNotification,
    notificationSettings.chatNotification,
    lastNotificationTime,
    setLastNotificationTime,
    prevNotiShow,
    prevMessageId,
    prevUnreadCount,
  ]);

  const handleClose = useCallback(
    (e) => {
      e.preventDefault();
      setNotiData({
        show: false,
        unRead: [],
        latestDoc: {} as any,
        id: '',
      });

      // 삭제시에도 unRead해제
      readChatCheck({ currentUserUid, docId: notiData.id });
    },
    [currentUserUid, notiData],
  );

  return (
    <SnackBar show={showNoti} setShow={setShowNoti} noDelay>
      <Link
        to={`/${CollectionPath.CHAT}`}
        onClick={() => {
          setChatDetailId(notiData.id);
          setNotiData({
            show: false,
            unRead: [],
            latestDoc: {} as any,
            id: '',
          });
        }}
      >
        <HeaderSec>
          <Icon name="Mail20" />
          <UserAvatar
            uid={notiData.latestDoc?.sender || ''}
            style={{ fontSize: 12 }}
            avatarSize={'24px'}
          />
        </HeaderSec>
        <MessagePreview
          dangerouslySetInnerHTML={{
            __html: replaceContents(notiData.latestDoc?.contents || ''),
          }}
        />

        <CloseBtn
          onClick={handleClose}
          shape="stroke"
          size="sm"
          style={{ position: 'absolute', top: -13, right: -13 }}
        />
      </Link>
    </SnackBar>
  );
}

export default ChatMessageNotification;
